import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { DynamicPropertyPage } from './DynamicPropertyPage';
import { API } from '../lib/api';
import { formatDateDDMMYYYY } from '../lib/utils';
import { ExternalLink, Loader2, PencilLine, ShieldCheck, ArrowLeft } from 'lucide-react';

export const ManagePropertyPage = ({ showPreview = false }) => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [manageData, setManageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  const fetchManageData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/modifications/manage/${token}`);
      setManageData(response.data);
    } catch (error) {
      console.error('Error loading manage page:', error);
      toast.error(error.response?.data?.detail || 'No se pudo abrir el enlace privado');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const checkPaymentStatus = useCallback(async (checkoutSessionId, attempts = 0) => {
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      toast.error('No pudimos confirmar el pago automáticamente. Si ya pagaste, escríbenos y lo revisamos.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/modifications/checkout/status/${checkoutSessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentConfirmed(true);
        toast.success('Pago confirmado. Revisaremos tus cambios y los publicaremos en cuanto estén validados.');
        setCheckingPayment(false);
        fetchManageData();
        return;
      }

      window.setTimeout(() => checkPaymentStatus(checkoutSessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking modification payment:', error);
      toast.error(error.response?.data?.detail || 'Error verificando el pago');
      setCheckingPayment(false);
    }
  }, [fetchManageData]);

  useEffect(() => {
    fetchManageData();
  }, [fetchManageData]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, checkPaymentStatus]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!manageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Enlace no disponible</CardTitle>
            <CardDescription>
              No hemos podido abrir este acceso privado. Si el enlace es antiguo o está incompleto, escríbenos y te lo reenviamos.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Show preview of the property without payment requirement
  if (showPreview && manageData) {
    return (
      <>
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 py-3 px-4 shadow-sm">
          <div className="mx-auto max-w-7xl flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Preview de cambios</h2>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/manage/${token}`)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
          </div>
        </div>
        <DynamicPropertyPage sampleProperty={manageData} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="mx-auto max-w-4xl space-y-8">
        {checkingPayment && (
          <div className="rounded-xl bg-blue-600 text-white px-5 py-4 shadow-lg flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Verificando tu pago...</span>
          </div>
        )}

        {paymentConfirmed && (
          <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4 text-green-900">
            <p className="font-semibold">Pago confirmado correctamente.</p>
            <p className="text-sm mt-1">Hemos recibido tus cambios y se publicarán una vez revisados.</p>
          </div>
        )}

        <section className="space-y-4 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-teal-50 text-teal-700 px-4 py-2 text-sm font-medium">
            <ShieldCheck className="h-4 w-4" />
            Enlace privado para gestionar futuras modificaciones
          </div>
          <h1 className="text-4xl font-bold text-gray-900">Editar mi web</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Desde este enlace puedes abrir tu formulario completo con los datos actuales precargados, editar lo necesario y confirmar los cambios por 15€.
          </p>
        </section>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PencilLine className="h-5 w-5 text-teal-600" />
                Abre tu formulario pre-rellenado
              </CardTitle>
              <CardDescription>
                Verás el mismo formulario de creación, pero con tus datos actuales. La licencia y el plan quedan bloqueados para mantener consistencia.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl border bg-gray-50 p-4 text-sm text-gray-600">
                Conserva este enlace. Es tu acceso privado para futuras modificaciones y evita pedir uno nuevo por email.
              </div>

              <div className="space-y-3">
                <Button
                  type="button"
                  size="lg"
                  className="w-full border-2 border-teal-600 text-teal-600 hover:bg-teal-50"
                  variant="outline"
                  onClick={() => navigate(`/manage/${token}/preview`)}
                  disabled={checkingPayment}
                >
                  Ver preview de los cambios
                </Button>

                <Button
                  type="button"
                  size="lg"
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                  onClick={() => navigate(`/manage/${token}/edit`)}
                  disabled={checkingPayment}
                >
                  <PencilLine className="mr-2 h-5 w-5" />
                  Editar mi web y pagar 15€
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{manageData.property_name}</CardTitle>
                <CardDescription>Licencia {manageData.license_number}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-600">
                <p><strong className="text-gray-900">Plan:</strong> {manageData.plan}</p>
                <p><strong className="text-gray-900">Email de contacto:</strong> {manageData.contact_email}</p>
                {manageData.published_url && (
                  <a
                    href={manageData.published_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-teal-700 hover:text-teal-800"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Ver tu web publicada
                  </a>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Qué pasa después</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-600">
                <p>1. Abres el formulario pre-rellenado con tus datos actuales.</p>
                <p>2. Editas textos, fotos y demás campos permitidos.</p>
                <p>3. Pagas los 15€ a través de Stripe para confirmar los cambios.</p>
                <p>4. Revisamos, regeneramos tu sitio y lo publicamos. Te avisamos por email en cuanto esté activo.</p>
              </CardContent>
            </Card>

            {manageData.latest_paid_request && (
              <Card>
                <CardHeader>
                  <CardTitle>Última solicitud pagada</CardTitle>
                  <CardDescription>
                    {formatDateDDMMYYYY(manageData.latest_paid_request.created_at)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-gray-600">
                  <p><strong className="text-gray-900">Resumen:</strong> {manageData.latest_paid_request.summary}</p>
                  <p><strong className="text-gray-900">Estado:</strong> {manageData.latest_paid_request.status}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
